import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { DalleImageTiles } from './dalle-image-tiles';

import { useImageBlockOperations } from '../helpers/block/use-image-block-operations';
import { Block, ImageBlockContent } from '../types';
import { useEditorStore } from '../editor-store';
import { NeueJourneyButton } from './neue-button/journey';
import { NeueListbox } from 'src/common/form/NeueListbox';
import { NeueJourneyLoadingSpinner } from './loading/journey-spinner';
import { ToggleButton } from './dashboard/create-journey-modal';
import { FileUploadForm } from './file-upload-form';
import { AutomagicRegularIcon } from 'src/monet/icons';

type Props = {
  block: Block;
  activeTab: 'ai' | 'upload';
  setActiveTab: (tab: 'ai' | 'upload') => void;
  onNewFile: (file: File) => void;
};

const DEFAULT_PROMPT = 'Renaissance painting of a landscape, dramatic';

type DalleFormProps = {
  block: Block;
  onDalleSubmit: (prompt: string, service: ImageOption) => void;
};

type ImageOption = 'dalle' | 'stability';

type Option = {
  label: string;
  value: ImageOption;
};

const ImageOptions: Option[] = [
  {
    label: 'Use DALL-E',
    value: 'dalle',
  },
  {
    label: 'Use Stable Diffusion',
    value: 'stability',
  }
];

export const DalleForm = ({ block, onDalleSubmit }: DalleFormProps) => {
  const content = block.content as ImageBlockContent;
  const [textInputValue, setTextInputValue] = useState(content.dalleParams?.prompt || '');
  const { loading: dalleLoading } = useImageBlockOperations(block);
  const [selectedImageOption, setSelectedImageOption] = useState<Option>(ImageOptions[0]);

  useEffect(() => {
    setTextInputValue(content.dalleParams?.prompt || '');
  }, [content.dalleParams?.prompt]);

  const onSubmit = (e: React.FormEvent<any>) => {
    e.preventDefault();
    const dallePrompt = (textInputValue || '').trim() || DEFAULT_PROMPT;
    setTextInputValue(dallePrompt);
    onDalleSubmit(dallePrompt, selectedImageOption.value);
  };

  return (
    <form className='flex flex-col gap-4' onSubmit={onSubmit}>
      <div className='flex flex-col gap-2'>
        <NeueListbox
          disabled={dalleLoading}
          selected={ImageOptions[0]}
          options={ImageOptions}
          renderValue={(o: Option) => o.label}
          renderKey={(o: Option) => o.value}
          onChange={(selected: Option) => {
            setSelectedImageOption(selected);
          }}
        />
      </div>
      <textarea
        disabled={dalleLoading}
        value={textInputValue}
        onChange={(e) => setTextInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            onSubmit(e);
          }
        }}
        className={classNames('neue-journey-text-input !py-3 h-18')}
        placeholder={DEFAULT_PROMPT}
      />
      <NeueJourneyButton disabled={dalleLoading} type='submit' className='h-10'>
        {dalleLoading ? <NeueJourneyLoadingSpinner size='16' /> : 'Generate'}
      </NeueJourneyButton>
    </form>
  );
};

export const AddImageControlPanelContent = ({ block, activeTab, setActiveTab, onNewFile }: Props) => {
  const content = block.content as ImageBlockContent;
  const selectBlock = useEditorStore((state) => state.selectBlock);

  const {
    empty: dalleEmpty,
    loading: dalleLoading,
    searchDalle,
    onNewImageUrl,
    imageTiles: dalleImageTiles,
  } = useImageBlockOperations(block);

  const onDalleSubmit = async (prompt: string, service: string) => {
    searchDalle(block, prompt, service);
    selectBlock(block.id);
  };

  const onSelectDalleImage = async (url: string, allUrls: string[]) => {
    onNewImageUrl({
      url,
      dalleParams: {
        ...content.dalleParams,
        urls: allUrls,
      },
    });
  };

  return (
    <>
      <div className='flex flex-row gap-2'>
        <ToggleButton disabled={dalleLoading} selected={activeTab === 'ai'} label={(
          <div className="flex items-center space-x-2">
            <AutomagicRegularIcon className="w-4 h-4 " />
            <div>AI Image</div>
          </div>
        )} onClick={() => setActiveTab('ai')} />
        <ToggleButton disabled={dalleLoading} selected={activeTab === 'upload'} label={(
          <div className="text-center">
            Upload
          </div>
          )} onClick={() => setActiveTab('upload')} />
      </div>
      {activeTab === 'ai' ? 
        <DalleForm block={block} onDalleSubmit={onDalleSubmit} /> :
        <FileUploadForm onNewFile={onNewFile} accept={'image/*'} />
      }
      {!dalleEmpty && activeTab === 'ai' && (
        <DalleImageTiles
          imageTiles={dalleImageTiles}
          selectedDalleUrl={content?.url || ''}
          onSelectDalleImage={onSelectDalleImage}
          dalleLoading={dalleLoading}
        />
      )}
    </>
  );
};
